import apiClient from './apiClient';
import { DriverLocation } from '../types/gps';
import config from '../config';

const API_URL = config.api.baseURL;

// 位置データの型定義
interface LocationData {
  latitude: number;
  longitude: number;
  driver_id?: number;
  timestamp?: string;
  [key: string]: any;
}

// ドライバーの最新位置情報を取得
export const getDriverLocations = async (): Promise<DriverLocation[]> => {
  try {
    const response = await apiClient.get<{ drivers: DriverLocation[] }>('/gps/drivers');
    return response.data.drivers;
  } catch (error: any) {
    console.error('ドライバーの位置情報取得中にエラーが発生しました:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || 'ドライバーの位置情報の取得に失敗しました');
  }
};

export const trackDriverLocation = async (locationData: LocationData) => {
  try {
    console.log('ドライバー位置情報をトラッキング中...');
    
    const response = await apiClient.post('/gps/log', locationData);
    
    console.log('Track Driver Location Response:', response);
    return response.data;
  } catch (error: any) {
    console.error('Track Driver Location Error:', error);
    
    // タイムアウトエラーやネットワークエラーの場合は成功として扱う（ローカルストレージに保存など）
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout') || 
        error.message?.includes('Network Error') || error.code === 'ERR_NETWORK') {
      console.warn('⚠️ ネットワークエラーのため、位置情報のローカル保存を実行');
      
      // ローカルストレージに位置情報を保存（後でサーバーに同期可能）
      const localData = JSON.parse(localStorage.getItem('pendingGPSData') || '[]');
      localData.push({
        ...locationData,
        timestamp: new Date().toISOString(),
        localOnly: true
      });
      localStorage.setItem('pendingGPSData', JSON.stringify(localData));
      
      return { success: true, message: 'ローカルに保存されました' };
    }
    
    throw error;
  }
};

// 位置共有設定（オン/オフ）を取得
export const getLocationSharing = async (): Promise<boolean> => {
  try {
    const response = await apiClient.get<{ location_sharing_enabled: boolean }>('/gps/sharing');
    return !!response.data.location_sharing_enabled;
  } catch (error: any) {
    console.error('位置共有設定の取得に失敗しました:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || '位置共有設定の取得に失敗しました');
  }
};

// 位置共有設定（オン/オフ）を更新
export const updateLocationSharing = async (enabled: boolean): Promise<boolean> => {
  try {
    const response = await apiClient.put<{ location_sharing_enabled: boolean }>('/gps/sharing', { enabled });
    return !!response.data.location_sharing_enabled;
  } catch (error: any) {
    console.error('位置共有設定の更新に失敗しました:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || '位置共有設定の更新に失敗しました');
  }
};

// 位置情報を追跡（送信）
export const trackLocation = async (locationData: { latitude: number; longitude: number; user_id: number }): Promise<void> => {
  try {
    await apiClient.post('/gps/track', locationData);
  } catch (error: any) {
    console.error('位置情報の送信に失敗しました:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || '位置情報の送信に失敗しました');
  }
};

// 特定のドライバーの位置履歴を取得
export const getDriverHistory = async (driverId: number) => {
  try {
    const response = await apiClient.get(`/gps/history/${driverId}`);
    return response.data;
  } catch (error: any) {
    console.error('ドライバーの位置履歴取得に失敗しました:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || 'ドライバーの位置履歴の取得に失敗しました');
  }
};

const GPSService = {
  getDriverLocations,
  trackDriverLocation
};

export default GPSService;
